import type React from 'react';
import { useLayoutEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { Chat } from '../../components/Chat';
import { usePanelStore } from '../../dualPanel';
import { DASHBOARDS_PATH } from '../../routes/paths';
import { DashboardCanvas } from '../components/DashboardCanvas';
import { DashboardPanelSurface } from '../components/DashboardPanel.styles';
import { useDashboardConversation } from '../hooks/useDashboardConversation';
import { useDashboardTemplates } from '../hooks/useDashboards';

/**
 * A single dashboard: pinned as a non-closable panel in the dual panel (left), which
 * therefore can't collapse, with the analytics chat on the right.
 */
export const DashboardView: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  // Templates are already loaded/cached by DashboardLayout, so this is instant.
  const { data: templates } = useDashboardTemplates();
  const { search } = useLocation();
  const { setDashboardPanel } = usePanelStore();
  useDashboardConversation(templateId);

  const name = templates?.find((template) => template.id === templateId)?.name;

  useLayoutEffect(() => {
    if (!templateId) return;
    setDashboardPanel({
      templateId,
      title: name ?? 'Dashboard',
      content: (
        <DashboardPanelSurface>
          <DashboardCanvas key={templateId} templateId={templateId} />
        </DashboardPanelSurface>
      ),
    });
    return () => setDashboardPanel(null);
  }, [templateId, name, setDashboardPanel]);

  if (!templateId) {
    return <Navigate to={{ pathname: DASHBOARDS_PATH, search }} replace />;
  }
  // Unknown id (e.g. a stale or hand-typed URL) → fall back to the index, which
  // redirects to the first available dashboard. The filters travel with it.
  if (templates && !templates.some((template) => template.id === templateId)) {
    return <Navigate to={{ pathname: DASHBOARDS_PATH, search }} replace />;
  }

  return <Chat showComposerContext={false} />;
};
